import { MouseEvent, useCallback } from 'react'

import { useMediaPlayerContext } from '@verbit-ai/verbit-connect-web/player-provider'

import { useFeatureFlag } from 'src/contexts/FeatureFlagContext'
import { useTranscriptTimings } from 'src/contexts/Transcript/TranscriptContext'
import { closestParagraphElement, paragraphPointAt } from 'src/utils/highlightRange'
import { TimingUtils } from 'src/utils/timings'

const PRIMARY_BUTTON = 0

export const useSeekOnClick = () => {
    const isTextHighlightingEnabled = useFeatureFlag('text_highlighting')
    const { mediaPlayer } = useMediaPlayerContext()
    const timings = useTranscriptTimings()

    return useCallback(
        (event: MouseEvent<HTMLElement>) => {
            const isPrimaryPress = event.button === PRIMARY_BUTTON
            // Shift extends a selection; Ctrl (macOS) and Cmd open menus or new tabs.
            const hasModifierKey = event.shiftKey || event.ctrlKey || event.metaKey
            // A drag that selected text is a copy, not a seek.
            const hasSelectedText = !window.getSelection()?.isCollapsed
            const shouldSeek = isTextHighlightingEnabled && isPrimaryPress && !hasModifierKey && !hasSelectedText
            if (!shouldSeek) {
                return
            }
            // Skips presses on things over the text, like the quick-action menu.
            const isPressOnParagraphText = event.target instanceof Node && closestParagraphElement(event.target) !== null
            if (!isPressOnParagraphText) {
                return
            }

            const paragraphPoint = paragraphPointAt(event.clientX, event.clientY)
            if (!paragraphPoint) {
                return
            }

            const timing = TimingUtils.getTimingByCharOffset(timings[paragraphPoint.paragraphId] ?? [], paragraphPoint.offset)
            if (timing) {
                mediaPlayer.seekTime(timing.mediaStart)
            }
        },
        [isTextHighlightingEnabled, mediaPlayer, timings],
    )
}
